/**
 * 到達性（presence）の取得・自動更新・表示直前の再判定
 *
 * ユーザー一覧とダッシュボードが**同一の取得結果を共有する**ため、取得は
 * 1 つのクエリ鍵（`['admin','presence']`）に集約する。別々に取ると、同じ画面の
 * 2 箇所が別の時点の判定を見ることになる。
 *
 * **サーバーが返した `status` をそのまま描画に使ってはならない。**
 * `usePresence` が返す `statusByEmail` は、表示のたびに最終確認時刻・しきい値・
 * 現在時刻から判定し直した結果である。保持している応答の `status` は
 * 「応答生成時点の判定」でしかなく、再取得が失敗し続けると古い「オンライン」を
 * 出し続ける。それは**表示層での偽陽性**であり、しかも画面上は正常に見えるため
 * 気付きにくい。再判定を通さない描画を新たに足さないこと。
 */
import type { GetAdminPresenceResponse, PresenceStatus } from '@kyumo/common';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchPresence } from '../services/admin-api';

/**
 * 到達性の再取得間隔（1 分）。
 *
 * **鮮度しきい値（180 秒）以下であること。** 超えると、しきい値を跨いだ状態変化を
 * 1 周期まるごと見落とす。検知の遅れは「しきい値 + この間隔」で決まる。
 */
export const PRESENCE_REFETCH_INTERVAL_MS = 60_000;

/**
 * 保持している応答を、現在時刻で判定し直す。
 *
 * **ブラウザの時計を絶対値として信用しない。** 端末の時計はずれうるため、
 * 応答を受け取った時点のローカル時刻と `generatedAt` の差を補正値として持ち、
 * 経過時間の測定にだけローカル時計を使う。時計が 1 日進んでいる端末で
 * `Date.now() - lastSeenAt` を直接見ると、全員が恒久的にオフラインになる。
 *
 * 時間の経過だけで `offline` から `online` へ遷移することはない。
 *
 * @param snapshot スキーマ検証済みの応答
 * @param receivedAtLocal 応答を受け取った時点のローカル時刻（Unix epoch ミリ秒）
 * @param nowLocal 現在のローカル時刻（Unix epoch ミリ秒）
 */
export function resolvePresenceAt(
  snapshot: GetAdminPresenceResponse,
  receivedAtLocal: number,
  nowLocal: number,
): Map<string, PresenceStatus> {
  // 受信時点でローカル時計がサーバーよりどれだけずれていたか。
  // 以降はこのずれを引いてサーバー時間軸へ戻す
  const skew = receivedAtLocal - snapshot.generatedAt;
  const nowOnServerAxis = nowLocal - skew;
  const thresholdMs = snapshot.thresholdSeconds * 1000;

  const result = new Map<string, PresenceStatus>();
  for (const entry of snapshot.entries) {
    const elapsed = nowOnServerAxis - entry.lastSeenAt;
    // サーバーが offline と判定したものは、時間が経ってもオンラインへは戻らない。
    // しきい値以内であっても、サーバーの判定より緩めることはしない（偽陽性の抑制を優先）
    const withinThreshold = elapsed <= thresholdMs;
    result.set(entry.email, entry.status === 'online' && withinThreshold ? 'online' : 'offline');
  }
  return result;
}

export interface UsePresenceResult {
  /** email から現在時刻で判定し直した状態への写像。含まれない利用者はオフライン。 */
  statusByEmail: Map<string, PresenceStatus>;
  /** email から最終確認時刻（Unix epoch ミリ秒）への写像。 */
  lastSeenByEmail: Map<string, number>;
  /**
   * 到達性の記録がある利用者数。
   *
   * **0 件は「誰も起動していない」ではない。** KeepAlive が 1 件も届いていない
   * 状態であり、配布・経路・認証を疑う合図になる。取得前・取得失敗時は undefined。
   */
  trackedUserCount: number | undefined;
  isLoading: boolean;
  isError: boolean;
}

export interface UsePresenceOptions {
  /**
   * 取得するかどうか。到達性の API は audit グループ所属者にしか応答しないため、
   * 非所属者では false を渡して権限エラーになる取得とポーリングを起こさない。
   */
  enabled?: boolean;
}

export function usePresence({ enabled = true }: UsePresenceOptions = {}): UsePresenceResult {
  const query = useQuery<GetAdminPresenceResponse>({
    queryKey: ['admin', 'presence'],
    queryFn: () => fetchPresence(),
    enabled,
    // 一定間隔で再取得する。
    // `refetchIntervalInBackground` は既定の false のままにする。**true にしてはならない。**
    // タブが非表示のあいだもポーリングし続けることになり、開きっぱなしのタブが
    // 無人で叩き続ける。再表示時の再取得は下の refetchOnWindowFocus が担う
    refetchInterval: PRESENCE_REFETCH_INTERVAL_MS,
    // **全体既定の staleTime（5 分）を引き継がないこと。** 引き継ぐと、タブが非表示の
    // あいだに定期取得が止まったまま、再表示しても 5 分以内なら再取得されない。
    // その間の再描画は数分前の応答を現在時刻で判定し直すため、KeepAlive を送り続けて
    // いる利用者が一斉にオフラインへ落ちて見える（次の定期取得で戻る）。
    // 1 周期より古い応答は古いものとして扱い、画面遷移時にも取り直させる
    staleTime: PRESENCE_REFETCH_INTERVAL_MS,
    // 再表示時は応答の新しさに関わらず必ず取り直す
    refetchOnWindowFocus: 'always',
    // 再取得のたびに読み込み表示へ戻さない（画面全体が再描画されたように見えるちらつきを防ぐ）。
    // 失敗しても useQuery は直前の data を保持するため、表示は消えない
    placeholderData: (previous) => previous,
  });

  // 応答を受け取った時点のローカル時刻。時計のずれの補正に使う。
  //
  // **取得完了の時刻をクエリキャッシュから取る（描画時に控えない）。**
  // 描画時に `Date.now()` を控えると、同じ応答を後から読む利用側
  // （例: ダッシュボードを開いたまま 1 分後にユーザー一覧へ移動した場合）が、
  // 応答の古さをそのまま「時計のずれ」として吸収してしまう。その分だけ経過時間が
  // 短く見積もられ、しきい値を過ぎた利用者がオンラインのまま残る（表示層での偽陽性）。
  // `dataUpdatedAt` は取得が成功した時点のローカル時刻であり、同じクエリ鍵を
  // 共有する全員が同じ基準点を見る。
  const snapshot = query.data;
  const receivedAtLocal = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;

  // **意図的に useMemo を使わない。** 判定の入力には現在時刻が含まれており、
  // 応答が変わらなくても答えは時間とともに変わる。応答を依存配列にして記憶すると、
  // 再取得が失敗し続けたときに古い「オンライン」が固まったまま残る。
  // 判定自体は件数ぶんのループでしかなく、レンダーごとに計算して困る重さではない。
  //
  // **再取得の応答待ちのあいだは、判定時刻を「受信から 1 周期後」までに留める。**
  // 定期取得が止まっていた後（タブの再表示など）は、応答が届くまでの再描画が
  // 古い応答を現在時刻で判定し、生きている利用者をオフラインに見せてしまう。
  // 1 周期後は定期取得が正常に回っているときに起こりうる最も古い判定であり、
  // それより緩めることはしない。再取得が失敗すれば isError として「不明」に倒れるため、
  // 古い「オンライン」が残り続けることはない
  const nowLocal = Date.now();
  const judgedAtLocal =
    query.isFetching && receivedAtLocal !== null
      ? Math.min(nowLocal, receivedAtLocal + PRESENCE_REFETCH_INTERVAL_MS)
      : nowLocal;
  const statusByEmail =
    snapshot && receivedAtLocal !== null
      ? resolvePresenceAt(snapshot, receivedAtLocal, judgedAtLocal)
      : new Map<string, PresenceStatus>();

  const lastSeenByEmail = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of snapshot?.entries ?? []) map.set(entry.email, entry.lastSeenAt);
    return map;
  }, [snapshot]);

  return {
    statusByEmail,
    lastSeenByEmail,
    // 取得できていない、あるいは失敗している状態を「記録 0 件」と混同させない
    trackedUserCount: query.isError || !snapshot ? undefined : snapshot.entries.length,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
